interface ObservationPollState {
  healthy: boolean;
  visible: boolean;
  connected: boolean;
}

/** One request at a time, with transport-aware reconciliation and visibility suspension. */
export function startObservationPoll(
  options: ObservationPollState & {
    request: () => Promise<boolean>;
    fallbackMs: number;
  },
): {
  update(state: ObservationPollState): void;
  refresh(): void;
  stop(): void;
} {
  let healthy = options.healthy;
  let visible = options.visible;
  let connected = options.connected;
  let stopped = false;
  let inFlight = false;
  let reconcilePending = false;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = null;
  };
  const schedule = () => {
    clear();
    if (stopped || !visible || inFlight) {
      return;
    }
    const base = healthy
      ? 60_000
      : Math.min(50_000, options.fallbackMs * 2 ** Math.min(failures, 3));
    timer = setTimeout(() => void run(), base + Math.floor(Math.random() * base * 0.2));
  };
  const run = async () => {
    clear();
    if (stopped || !visible) {
      return;
    }
    if (inFlight) {
      reconcilePending = true;
      return;
    }
    inFlight = true;
    try {
      failures = (await options.request()) ? 0 : failures + 1;
    } catch {
      failures++;
    } finally {
      inFlight = false;
      if (!stopped && visible && reconcilePending) {
        reconcilePending = false;
        void run();
      } else {
        schedule();
      }
    }
  };
  void run();
  return {
    refresh() {
      void run();
    },
    update({ healthy: nextHealthy, visible: nextVisible, connected: nextConnected }) {
      // Fresh data returned by REST already reconciled the source. Only a
      // transport reconnect or returning to the tab needs another snapshot.
      const reconcile = (!visible && nextVisible) || (!connected && nextConnected);
      const changed = healthy !== nextHealthy || visible !== nextVisible;
      healthy = nextHealthy;
      visible = nextVisible;
      connected = nextConnected;
      if (!visible) {
        reconcilePending = false;
        clear();
      } else if (reconcile) {
        void run();
      } else if (changed) {
        schedule();
      }
    },
    stop() {
      stopped = true;
      reconcilePending = false;
      clear();
    },
  };
}
